// app/features/ai-workflows/components/PersonalizedUseCaseListComponent.tsx
// app/features/personalized-use-cases/components/PersonalizedUseCaseListComponent.tsx
"use client";

import {
  AlertTriangle,
  CheckCircle,
  Inbox,
  Loader2,
  PlusCircle,
  Search,
  Sparkles,
} from "lucide-react";
import React, { useMemo, useState } from "react";

import { HTFButton } from "@/app/shared/components/button/HTFButtonComponent";
import { HTFContentCard } from "@/app/shared/components/cards/HTFContentCard";
import { HTFSectionHeader } from "@/app/shared/components/layout/HTFSectionHeader";
import { Button } from "@/components/ui/button";
import { LoadingIndicator } from "@/components/ui/loading-indicator";
import { Pagination } from "@/components/ui/pagination";
import { cn } from "@/lib/utils";
import { type Database } from "@/types/supabase";

import { PersonalizedUseCaseCardComponent } from "./PersonalizedUseCaseCardComponent";
import { PersonalizedUseCaseGeneratingCardComponent } from "./PersonalizedUseCaseGeneratingCardComponent";

type PersonalizedUseCase =
  Database["public"]["Tables"]["personalized_use_cases"]["Row"];

interface PersonalizedUseCaseListComponentProps {
  useCases: PersonalizedUseCase[] | null | undefined;
  totalCount: number;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  onCreateNew: () => void;
  // Pagination props
  pagination: {
    currentPage: number;
    totalPages: number;
    recordsPerPage: 12 | 48;
    totalRecords: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
    goToPage: (page: number) => void;
    changeRecordsPerPage: (recordsPerPage: 12 | 48) => void;
  };
}

// 🔥 IMPROVED: Better status priority for sorting - generating statuses get top priority
const getStatusPriority = (status: string | null): number => {
  switch (status) {
    // 🔥 CRITICAL: Generating statuses in CORRECT workflow order (1-9)
    case "pending":
      return 1;
    case "enhancing_problem":
      return 2;
    case "generating_problem":
      return 3; // Step 1/4
    case "conceptual_workflows_generated":
      return 4; // Step 2/4 - FIXED ORDER
    case "workflows_generated_and_refined":
      return 5; // Step 3/4 - FIXED ORDER
    case "generating_workflows":
      return 6; // Legacy status
    case "generating_content":
      return 7; // Alternative Step 4
    case "generating_full_workflows":
      return 8; // Step 4/4 - FIXED ORDER
    case "assembling":
      return 9;
    case "regenerating":
      return 10;
    // Completed statuses come after generating
    case "generated":
      return 11;
    case "completed":
      return 11;
    // Failed comes last
    case "failed":
      return 12;
    default:
      return 2; // Default to pending priority for unknown statuses
  }
};

// Helper function to check if use case is completed
const isUseCaseCompleted = (useCase: PersonalizedUseCase): boolean => {
  // 🔥 CRITICAL FIX: A use case with 'completed' status should always be considered completed,
  // even if the title hasn't been updated in the cache yet due to race conditions
  if (useCase.generation_status === "completed") {
    return true;
  }

  // For 'generated' status, we still require a title since that was the legacy check
  if (useCase.generation_status === "generated" && !!useCase.title) {
    return true;
  }

  return false;
};

// 🔥 IMPROVED: More comprehensive generating status check with all production statuses
const isUseCaseGenerating = (useCase: PersonalizedUseCase): boolean => {
  const generatingStatuses = [
    "pending",
    "enhancing_problem",
    "generating_problem",
    "generating_workflows",
    "conceptual_workflows_generated", // From logs - intermediate status
    "workflows_generated_and_refined", // 🔥 FIXED: Missing from logs!
    "generating_content",
    "assembling", // 🔥 CRITICAL: Keep assembling as generating since it's the final generation step
    "regenerating",
    "generating_full_workflows",
  ];
  return generatingStatuses.includes(useCase.generation_status || "");
};

// Helper function to check if use case is failed
const isUseCaseFailed = (useCase: PersonalizedUseCase): boolean => {
  return useCase.generation_status === "failed";
};

export function PersonalizedUseCaseListComponent({
  useCases,
  totalCount,
  isLoading,
  isError,
  error,
  onCreateNew,
  pagination,
}: PersonalizedUseCaseListComponentProps) {
  const [searchQuery, setSearchQuery] = useState("");

  // Filter and search logic
  const filteredUseCases = useMemo(() => {
    if (!useCases) return [];

    let filtered = useCases;

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (useCase) =>
          useCase.title?.toLowerCase().includes(query) ||
          useCase.user_input_problem_statement?.toLowerCase().includes(query) ||
          useCase.user_input_desired_outcome?.toLowerCase().includes(query),
      );
    }

    // 🔥 IMPROVED: Sort by status priority first, then by creation date
    return [...filtered].sort((a, b) => {
      const priorityA = getStatusPriority(a.generation_status);
      const priorityB = getStatusPriority(b.generation_status);

      if (priorityA !== priorityB) {
        return priorityA - priorityB; // Lower priority number = higher in list
      }

      // If same status priority, sort by creation date (newest first)
      return (
        new Date(b.created_at || "").getTime() -
        new Date(a.created_at || "").getTime()
      );
    });
  }, [useCases, searchQuery]);

  // 🔥 CRITICAL: Always include all use cases in their respective categories
  const generatedUseCases = filteredUseCases.filter(isUseCaseCompleted);
  const generatingUseCases = filteredUseCases.filter(isUseCaseGenerating);
  const failedUseCases = filteredUseCases.filter(isUseCaseFailed);

  // 🔥 DEBUG: Log categorization results
  console.log("[ListComponent] Categorization:", {
    total: filteredUseCases.length,
    generating: generatingUseCases.length,
    completed: generatedUseCases.length,
    failed: failedUseCases.length,
    generatingIds: generatingUseCases.map((uc) => ({
      id: uc.id,
      status: uc.generation_status,
    })),
  });

  // Categorization complete - organized into proper sections

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex justify-center items-center py-16">
          <LoadingIndicator text="Loading your use cases..." />
        </div>
      );
    }

    if (isError) {
      return (
        <HTFContentCard variant="error" padded bordered className="text-center">
          <div className="flex flex-col items-center gap-2">
            <AlertTriangle className="w-8 h-8 text-error" />
            <p className="font-semibold">Error Loading Use Cases</p>
            <p className="text-sm">
              {error?.message || "An unexpected error occurred."}
            </p>
          </div>
        </HTFContentCard>
      );
    }

    if (!useCases || useCases.length === 0) {
      return (
        <HTFContentCard variant="muted" padded bordered className="text-center">
          <div className="flex flex-col items-center gap-3 py-8">
            <Inbox className="w-12 h-12 text-gray-400" />
            <p className="font-semibold text-gray-700">
              No Personalized Use Cases Yet
            </p>
            <p className="text-sm text-gray-500 max-w-sm">
              Click the button above to generate your first AI use case tailored
              to your business needs.
            </p>
            <HTFButton
              onClick={onCreateNew}
              variant="primary"
              icon={<PlusCircle className="w-4 h-4" />}
              className="w-full sm:w-auto"
            >
              Generate New AI Workflow
            </HTFButton>
          </div>
        </HTFContentCard>
      );
    }

    if (filteredUseCases.length === 0 && !isLoading) {
      return (
        <HTFContentCard variant="muted" padded bordered className="text-center">
          <div className="flex flex-col items-center gap-3 py-8">
            <Search className="w-12 h-12 text-gray-400" />
            <p className="font-semibold text-gray-700">No Use Cases Found</p>
            <p className="text-sm text-gray-500">
              Try adjusting your search criteria.
            </p>
            <Button
              onClick={() => setSearchQuery("")}
              variant="outline"
              className="mt-2"
            >
              Clear Search
            </Button>
          </div>
        </HTFContentCard>
      );
    }

    return (
      <div className="space-y-10">
        {/* 🔥 CRITICAL: Show generating use cases FIRST */}
        {generatingUseCases.length > 0 && (
          <section>
            <HTFSectionHeader
              icon={Loader2}
              iconClassName="text-blue-600 animate-spin"
              title="In Progress"
              subtitle={`${generatingUseCases.length} use case${generatingUseCases.length === 1 ? "" : "s"} currently generating. This typically takes 5-8 minutes.`}
              size="md"
              className="mb-6"
            />
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {generatingUseCases.map((useCase) => (
                <PersonalizedUseCaseGeneratingCardComponent
                  key={useCase.id}
                  useCase={useCase}
                />
              ))}
            </div>
          </section>
        )}

        {/* Generated/Ready use cases come second */}
        {generatedUseCases.length > 0 && (
          <section>
            <HTFSectionHeader
              icon={CheckCircle}
              iconClassName="text-green-600"
              title="Ready to Implement"
              subtitle={`${generatedUseCases.length} use case${generatedUseCases.length === 1 ? "" : "s"} generated and ready to use.`}
              size="sm"
              className="mb-6"
              fullWidthLine={true}
            />
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {generatedUseCases.map((useCase) => (
                <PersonalizedUseCaseCardComponent
                  key={useCase.id}
                  useCase={useCase}
                />
              ))}
            </div>
          </section>
        )}

        {/* Failed use cases come last */}
        {failedUseCases.length > 0 && (
          <section>
            <HTFSectionHeader
              icon={AlertTriangle}
              iconClassName="text-red-600"
              title="Failed"
              subtitle={`${failedUseCases.length} use case${failedUseCases.length === 1 ? "" : "s"} failed to generate. You can restart them.`}
              size="md"
              className="mb-6"
            />
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {failedUseCases.map((useCase) => (
                <PersonalizedUseCaseCardComponent
                  key={useCase.id}
                  useCase={useCase}
                />
              ))}
            </div>
          </section>
        )}
      </div>
    );
  };

  return (
    <div className="w-full">
      <div className="w-full max-w-none 2xl:max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-16 py-6 md:py-10">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8">
          <HTFSectionHeader
            title="Your AI Workflows"
            subtitle="Your personal AI consultant. Describe a problem, get a step-by-step recipe to solve it with AI."
            icon={Sparkles}
            divider={false}
            className="mb-0 shrink-0"
          />

          <div className="flex flex-col sm:flex-row items-center gap-4 w-full md:w-auto">
            {useCases && useCases.length > 0 && (
              <div className="relative w-full sm:w-64">
                <Search
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                  size={18}
                />
                <input
                  type="search"
                  placeholder="Search use cases..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={cn(
                    "w-full h-10 pl-10 pr-3 py-2 text-sm rounded-md shadow-sm",
                    "bg-[#fafafa] text-gray-900 border-gray-300",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:border-transparent",
                    "hover:border-primary transition-colors",
                  )}
                />
              </div>
            )}

            <HTFButton
              onClick={onCreateNew}
              variant="primary"
              icon={<PlusCircle className="w-4 h-4" />}
              className="w-full sm:w-auto"
            >
              Generate New AI Workflow
            </HTFButton>
          </div>
        </div>

        {renderContent()}

        {/* Add pagination if there are use cases and more than one page */}
        {useCases && useCases.length > 0 && pagination.totalPages > 1 && (
          <div className="mt-8 flex justify-center">
            <Pagination
              currentPage={pagination.currentPage}
              totalPages={pagination.totalPages}
              recordsPerPage={pagination.recordsPerPage}
              totalRecords={pagination.totalRecords}
              hasNextPage={pagination.hasNextPage}
              hasPrevPage={pagination.hasPrevPage}
              onPageChange={pagination.goToPage}
              onRecordsPerPageChange={pagination.changeRecordsPerPage}
            />
          </div>
        )}
      </div>
    </div>
  );
}
